/* eslint-disable no-param-reassign */
import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { filterType } from '../../config/enum';
import {
  AvailableDevice,
  DeviceInitialProperties,
  DevicePointsInterface,
  FetchDeviceOnlyScheduleProperties,
  FetchDeviceProperties,
  FilterDeviceInterFace,
  RequestDeviceInterFace,
} from '../../interface/partners-device-management.interface';
import {
  CommonFilterProperties,
  FilterUpdateProperties,
} from '../../interface/partners-filter.interface';
import { removeEmptyKeys } from '../../shared-functions/DataTime';
import {
  ClearFilters,
  ConfigureListFilterResponse,
  RemoveSingleFilterFromList,
  ReplaceValueInFilter,
  RestFilterAndAddSingleFilter,
  UpdateCommonFilter,
  MergeCommonFilters,
} from '../../shared-functions/PartnersCommonFilters';
import deviceApi from '../../utils/api/partner/device-partner';

// a thunk to fetch devices
export const fetchAllDevices = createAsyncThunk<FetchDeviceProperties, RequestDeviceInterFace>(
  'accountPayoutDevice/fetchAllDevices',
  async (data: RequestDeviceInterFace, { dispatch }) => {
    try {
      const response = await deviceApi.fetchDevices(removeEmptyKeys(data));
      return {
        ...response.data,
        results: response.data.results.map((deviceData: AvailableDevice) => {
          const connectorsStatus = deviceData.connectorsStatus || {};
          const rawDeviceData = deviceData as any;
          return {
            ...deviceData,
            id: deviceData._id,
            chargingStationName: Array.isArray(rawDeviceData.chargingStationName)
              ? rawDeviceData.chargingStationName[0] || ''
              : rawDeviceData.chargingStationName || '',
            connectorsStatus,
            connector1: connectorsStatus.connector1 || '',
            connector2: connectorsStatus.connector2 || '',
          };
        }),
      };
    } catch (error: any) {
      dispatch({
        type: 'notification/setNotificationDetails',
        payload: {
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        },
      });
      throw error;
    }
  },
);

export const refreshDevices = createAsyncThunk<FetchDeviceProperties, RequestDeviceInterFace>(
  'accountPayoutDevice/refreshDevices',
  async (data: RequestDeviceInterFace) => {
    const response = await deviceApi.fetchDevices(removeEmptyKeys(data));

    return {
      ...response.data,
      results: response.data.results.map((deviceData: AvailableDevice) => {
        const connectorsStatus = deviceData.connectorsStatus || {};
        const rawDeviceData = deviceData as any;
        return {
          ...deviceData,
          id: deviceData._id,
          chargingStationName: Array.isArray(rawDeviceData.chargingStationName)
            ? rawDeviceData.chargingStationName[0] || ''
            : rawDeviceData.chargingStationName || '',
          connectorsStatus,
          connector1: connectorsStatus.connector1 || '',
          connector2: connectorsStatus.connector2 || '',
        };
      }),
    };
  },
);

// a thunk to fetch devices filters
export const fetchListFilters = createAsyncThunk<
  CommonFilterProperties[], // Return type
  void // No need for an argument, since we are not using the payload
>('accountPayoutDevice/fetchListFilters', async (_, { dispatch }) => {
  try {
    const response = await deviceApi.fetchListFilters();
    const updatedResponse: CommonFilterProperties[] = ConfigureListFilterResponse(response.data);
    return updatedResponse;
  } catch (error: any) {
    dispatch({
      type: 'notification/setNotificationDetails',
      payload: {
        type: 'error',
        notificationDetails: {
          statusCode: error?.response?.data?.code || 200,
          message: error?.response?.data?.message || 'Something went wrong!',
        },
      },
    });
    throw error;
  }
});

// a thunk to fetch all stations
export const fetchAllDevicesList = createAsyncThunk<
  { _id: string; chargingPointSn: string; chargingStationId: string }[],
  FetchDeviceOnlyScheduleProperties
>('accountPayoutDevice/fetchAllDevicesList', async (data: FetchDeviceOnlyScheduleProperties) => {
  if (data.ids && data.ids.length > 0) {
    const responses = await Promise.all(
      data.ids.map(async (stationId) => {
        const response = await deviceApi.fetchAllDevicesList(stationId);
        return response.data.map((device: any) => ({
          ...device,
          chargingStationId: stationId,
        }));
      }),
    );
    const allDevices = responses.flat();
    return allDevices;
  }
  if (data.id) {
    const response = await deviceApi.fetchAllDevicesList(data.id);
    return response.data.map((device: any) => ({
      ...device,
      chargingStationId: data.id,
    }));
  }
  return [];
});

// a thunk to fetch all devices
export const fetchOneDevicePoints = createAsyncThunk<
  { _id: string; connectors: DevicePointsInterface[] } | null,
  FetchDeviceOnlyScheduleProperties
>('accountPayoutDevice/fetchOneDevicePoints', async (data: FetchDeviceOnlyScheduleProperties) => {
  if (data.id) {
    const response = await deviceApi.fetchOneDevicePoints(data.id);
    return response.data;
  }
  return null;
});

// a thunk to fetch all deviceModels
export const fetchAllDeviceModels = createAsyncThunk<string[]>(
  'accountPayoutDevice/fetchAllDeviceModels',
  async () => {
    const response = await deviceApi.fetchAllDeviceModels();
    return response.data;
  },
);

// a thunk to fetch all deviceModels
export const fetchChargePointVendors = createAsyncThunk<string[]>(
  'accountPayoutDevice/fetchChargePointVendors',
  async () => {
    const response = await deviceApi.fetchAllChargePointVendors();
    return response.data;
  },
);

// a thunk to fetch all deviceModels
export const fetchChargePointModels = createAsyncThunk<string[], string>(
  'accountPayoutDevice/fetchChargePointModels',
  async (vendor) => {
    const response = await deviceApi.fetchAllChargePointModels(vendor);
    return response.data;
  },
);

const deviceFilter: CommonFilterProperties[] = [
  {
    name: 'searchPlaceholder',
    key: 'searchBy',
    type: filterType.TEXT,
    value: '',
    applyFilter: false,
    enable: true,
    searchBy: true,
  },
];

// Initial state
const initialState: DeviceInitialProperties = {
  devices: [],
  allDevices: [],
  allDeviceModels: [],
  chargePointModels: [],
  chargePointVendors: [],
  devicePoints: [],
  deviceId: '',
  loading: false,
  page: DefaultApiLimit.page,
  limit: DefaultApiLimit.limit,
  totalPages: 1,
  totalResults: 0,
  filter: {},
  filters: deviceFilter,
  filterListLoading: false,
  isFilterUpdated: false,
  sortBy: '',
  sortField: '',
  totalDeviceCount: 0,
  totalOfflineDeviceCount: 0,
  totalOnlineDeviceCount: 0,
};

// Create the slice
export const accountPayoutDeviceSlice = createSlice({
  name: 'accountPayoutDevice',
  initialState,
  reducers: {
    /**
     * Change the device filter status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setDeviceFilterData: (
      state: DeviceInitialProperties,
      action: PayloadAction<FilterDeviceInterFace>,
    ) => {
      state.filter = action.payload;
    },
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    changeDevicePointStatus: (state, action) => {
      state.devicePoints = state.devicePoints.map((value) => {
        const output = value;
        if (value.connector === action.payload.id) {
          output.isReservationEnabled = action.payload.value === 'available';
        }
        return {
          ...output,
        };
      });
    },
    /**
     * Update device management filters
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    updateDeviceFilters: (state, action: PayloadAction<FilterUpdateProperties>) => {
      const payload = { ...action.payload };
      if (payload.type === 'add') {
        const { updatedCommonFilter, isFilterUpdated } = UpdateCommonFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'remove') {
        state.filters = RemoveSingleFilterFromList(state.filters, payload.key);
        state.isFilterUpdated = true;
      } else if (payload.type === 'clear') {
        state.filters = ClearFilters(state.filters);
        state.isFilterUpdated = true;
      } else if (payload.type === 'replace') {
        let { updatedCommonFilter, isFilterUpdated } = ReplaceValueInFilter(
          state.filters,
          action.payload,
        );
        if (!isFilterUpdated && payload.key) {
          const newFilter: CommonFilterProperties = {
            name: payload.key,
            key: payload.key,
            type: Array.isArray(payload.value) ? filterType.MULTISELECT : filterType.TEXT,
            value: payload.value,
            valueToShow:
              payload.valueToShow ||
              (Array.isArray(payload.value) ? payload.value : [payload.value]),
            applyFilter: true,
            enable: true,
            FEAddedFilter: true,
          };
          updatedCommonFilter = [...state.filters, newFilter];
          isFilterUpdated = true;
        }
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      } else if (payload.type === 'restAndAdd') {
        const { updatedCommonFilter, isFilterUpdated } = RestFilterAndAddSingleFilter(
          state.filters,
          action.payload,
        );
        state.filters = updatedCommonFilter;
        state.isFilterUpdated = isFilterUpdated;
      }
    },
    /**
     * Change the device point status function
     * @param {any} state - The current state.
     */
    removeDevicePointStatus: (state) => {
      state.devicePoints = [];
      state.deviceId = '';
    },
    /**
     * Function that reset slice to initial state
     * @param {any} state - The current state.
     */
    resetDeviceToInitialState: (state) => {
      Object.assign(state, initialState);
    },
    /**
     * function for set sortBy
     * @param {any} state - The current state.
     * @param {any} action - The action object.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortBy = action.payload.sortBy;
      state.sortField = action.payload.sortField;
    },
  },
  /**
   * Extra reducers to handle thunk request
   * @param {any} builder - The builder object.
   */
  extraReducers: (builder: ActionReducerMapBuilder<DeviceInitialProperties>) => {
    builder
      .addCase(fetchAllDevices.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchAllDevices.fulfilled, (state, action: PayloadAction<FetchDeviceProperties>) => {
        state.loading = false;
        state.page = action.payload.page;
        state.limit = action.payload.limit;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
        state.devices = action.payload.results;
        state.totalDeviceCount = action.payload.totalDeviceCount;
        state.totalOnlineDeviceCount = action.payload.totalOnlineDeviceCount;
        state.totalOfflineDeviceCount = action.payload.totalOfflineDeviceCount;
      })
      .addCase(fetchAllDevices.rejected, (state) => {
        state.loading = false;
      });
    builder.addCase(fetchAllDevicesList.fulfilled, (state, action) => {
      state.loading = false;
      state.allDevices = action.payload.map(
        (stationData: { _id: string; chargingPointSn: string; chargingStationId: string }) => ({
          value: stationData._id,
          label: stationData.chargingPointSn,
          stationId: stationData.chargingStationId,
        }),
      );
    });
    builder.addCase(fetchOneDevicePoints.fulfilled, (state, action) => {
      state.loading = false;
      if (action.payload) {
        state.devicePoints = action.payload.connectors;
        state.deviceId = action.payload._id;
      }
    });
    builder.addCase(fetchAllDeviceModels.fulfilled, (state, action) => {
      state.loading = false;
      state.allDeviceModels = action.payload.map((device: string) => ({
        value: device,
        label: device,
      }));
    });
    builder.addCase(fetchChargePointVendors.fulfilled, (state, action) => {
      state.chargePointVendors = action.payload.map((device: string) => ({
        value: device,
        label: device,
      }));
    });
    builder.addCase(fetchChargePointModels.fulfilled, (state, action) => {
      state.chargePointModels = action.payload.map((device: string) => ({
        value: device,
        label: device,
      }));
    });
    builder
      .addCase(fetchListFilters.pending, (state) => {
        state.filterListLoading = true;
      })
      .addCase(
        fetchListFilters.fulfilled,
        (state, action: PayloadAction<CommonFilterProperties[]>) => {
          state.filterListLoading = false;
          state.filters = MergeCommonFilters(state.filters, action.payload);
        },
      )
      .addCase(fetchListFilters.rejected, (state) => {
        state.filterListLoading = false;
      });

    builder.addCase(refreshDevices.fulfilled, (state, action) => {
      state.devices = action.payload.results;
      state.totalResults = action.payload.totalResults;
      state.totalPages = action.payload.totalPages;
    });
  },
});

// Export the actions
export const {
  changeDevicePointStatus,
  removeDevicePointStatus,
  setDeviceFilterData,
  resetDeviceToInitialState,
  updateDeviceFilters,
  setSortData,
} = accountPayoutDeviceSlice.actions;

// Export the reducer
export default accountPayoutDeviceSlice.reducer;
